import Phaser from "phaser";
import {
  CATEGORY_KIND,
  COLORS,
  COMBO_MULTS,
  COMBO_WINDOW_MS,
  DIGEST_MS,
  ENEMY_FIRE_RANGE,
  ENEMY_SHOT_DAMAGE,
  ENEMY_SHOT_SPEED,
  ENEMY_SHOTS_PER_VOLLEY_MAX,
  ENEMY_VALUE_BASE,
  ENEMY_VOLLEY_MS,
  ENGULF_GRACE_MS,
  ENGULF_PAD,
  FIRE_INTERVAL_MS,
  FIRE_RANGE,
  GAME_HEIGHT,
  GAME_WIDTH,
  LUNGS_REGION,
  MISSION_COMPLETE_BONUS,
  NEUTROPHIL,
  PATHOGENS,
  PAUSE_BREATHE_BONUS,
  POP_MS,
  REGIONS,
  SHOT_SPEED,
  STRENGTH_GAIN,
  TRACTOR_MS,
} from "../config";
import PlayerCell from "../entities/PlayerCell";
import Pathogen, { PATHOGEN_GONE } from "../entities/Pathogen";
import Shot from "../entities/Shot";
import EnemyShot from "../entities/EnemyShot";
import { AudioFx } from "../systems/AudioFx";
import HealingFeedback from "../systems/HealingFeedback";
import { buildPopulation } from "../systems/MapPopulator";
import { drawRegionBackground } from "../systems/RegionBackground";
import Pickups from "../systems/Pickups";
import { SaveState } from "../systems/SaveState";
import Joystick from "../ui/Joystick";
import Hud from "../ui/Hud";
import Minimap from "../ui/Minimap";
import type { BreathMode, GameMode, RegionDef, RunResult } from "../types";

/** A static region. Combat is either "engulf" (Lungs) or "shoot" (Intestines). */
export default class GameScene extends Phaser.Scene {
  private region: RegionDef = LUNGS_REGION;
  private mode: GameMode = "engulf";
  private player!: PlayerCell;
  private pathogens!: Phaser.GameObjects.Group;
  private pickups!: Pickups;
  private feedback!: HealingFeedback;
  private joystick!: Joystick;
  private hud!: Hud;
  private minimap!: Minimap;

  private totalEnemies = 0;
  private pointsEarned = 0;
  private clearedCount = 0;
  private revives = 0;

  // engulf
  private pending: { p: Pathogen; t0: number } | null = null;
  private comboCount = 0;
  private comboExpireAt = 0;
  // shoot
  private nextFireAt = 0;
  private nextVolleyAt = 0;

  private breatheUsed = false;
  private breathing = false;
  private menuOpen = false;
  private won = false;

  constructor() {
    super("Game");
  }

  init(data: { regionId?: string }): void {
    this.region = (data.regionId && REGIONS[data.regionId]) || LUNGS_REGION;
    this.mode = this.region.mode;
  }

  create(): void {
    this.resetRunState();

    const { width, height } = this.region.map;
    drawRegionBackground(this, this.region);
    this.physics.world.setBounds(0, 0, width, height);
    this.cameras.main.setBounds(0, 0, width, height);

    this.player = new PlayerCell(this, width / 2, height / 2, NEUTROPHIL, this.mode);
    this.cameras.main.startFollow(this.player, true, 0.14, 0.14);

    this.pathogens = this.add.group();
    this.pickups = new Pickups(this, this.player, this.mode);
    this.feedback = new HealingFeedback(this);
    this.joystick = new Joystick(this);
    this.hud = new Hud(this, () => this.openPauseBreathe(), () => this.openQuitConfirm());
    this.minimap = new Minimap(this, this.region.map);

    this.populate();

    this.events.on(PATHOGEN_GONE, this.onEnemyGone, this);
    this.feedback.setInflammation(1);
    this.cameras.main.fadeIn(350, 5, 12, 26);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.events.off(PATHOGEN_GONE, this.onEnemyGone, this));
  }

  private resetRunState(): void {
    this.totalEnemies = 0;
    this.pointsEarned = 0;
    this.clearedCount = 0;
    this.revives = 0;
    this.pending = null;
    this.comboCount = 0;
    this.comboExpireAt = 0;
    this.nextFireAt = 0;
    this.nextVolleyAt = 0;
    this.breatheUsed = false;
    this.breathing = false;
    this.menuOpen = false;
    this.won = false;
  }

  private populate(): void {
    const placements = buildPopulation(this.region, ENEMY_VALUE_BASE);
    for (const pl of placements) {
      const kind = pl.tier === "apex" ? "apex" : pl.tier === "elite" ? "elite" : CATEGORY_KIND[pl.category];
      const p = new Pathogen(this, pl.x, pl.y, PATHOGENS[kind], pl.strength, {
        tier: pl.tier,
        player: this.player,
        showSafety: this.mode === "engulf",
      });
      this.pathogens.add(p);
    }
    this.totalEnemies = placements.length;
  }

  update(time: number, _delta: number): void {
    if (this.breathing || this.won || this.menuOpen) return;
    this.player.drive(this.joystick.vector.x, this.joystick.vector.y);

    if (this.mode === "shoot") {
      this.autoFire(time);
      this.enemyFire(time);
    } else {
      this.updateEngulf(time);
      if (this.comboCount > 0 && time > this.comboExpireAt) this.comboCount = 0;
    }

    this.updateMinimap();
    this.updateHud(time);
  }

  private remaining(): number {
    let n = 0;
    for (const child of this.pathogens.getChildren()) {
      const p = child as Pathogen;
      if (p.active && !p.removed) n++;
    }
    return n;
  }

  private nearest(range: number): Pathogen | null {
    let best: Pathogen | null = null;
    let bestDist = range;
    for (const child of this.pathogens.getChildren()) {
      const p = child as Pathogen;
      if (!p.active || p.removed) continue;
      const d = p.distanceTo(this.player.x, this.player.y);
      if (d <= bestDist) {
        best = p;
        bestDist = d;
      }
    }
    return best;
  }

  // ── ENGULF combat ─────────────────────────────────────────────────────────────

  private updateEngulf(time: number): void {
    if (this.pending) {
      const p = this.pending.p;
      const range = this.player.effectiveRadius + p.def.radius + ENGULF_PAD;
      if (!p.active || p.removed) this.pending = null;
      else if (p.distanceTo(this.player.x, this.player.y) > range * 1.35) this.pending = null;
      else {
        p.pullToward(this.player.x, this.player.y, 0.18);
        if (time - this.pending.t0 >= TRACTOR_MS) {
          this.resolveEngulf(p, time);
          this.pending = null;
        }
      }
      return;
    }
    if (!this.player.canEngulf()) return;
    const target = this.nearest(this.player.effectiveRadius + 60 + ENGULF_PAD);
    if (target) this.pending = { p: target, t0: time };
  }

  private resolveEngulf(p: Pathogen, time: number): void {
    if (this.player.strength > p.value) this.winEngulf(p, time);
    else if (this.player.hasShield(time)) {
      this.player.consumeShield();
      this.winEngulf(p, time, true);
    } else this.failEngulf();
  }

  private winEngulf(p: Pathogen, time: number, viaShield = false): void {
    this.player.win(STRENGTH_GAIN[p.tier]);
    this.comboCount = time <= this.comboExpireAt ? this.comboCount + 1 : 1;
    this.comboExpireAt = time + COMBO_WINDOW_MS;
    const mult = COMBO_MULTS[Math.min(this.comboCount - 1, COMBO_MULTS.length - 1)];
    const pts = Math.round(p.points * mult);
    this.pointsEarned += pts;
    SaveState.addPoints(pts);
    this.player.beginDigesting();
    p.engulf(this.player, DIGEST_MS);
    AudioFx.engulf();
    this.floatScore(p.x, p.y, pts, mult);
    if (viaShield) this.cameras.main.flash(200, 159, 232, 255);
  }

  private failEngulf(): void {
    this.comboCount = 0;
    this.cameras.main.flash(260, 192, 48, 58);
    AudioFx.hurt();
    this.enterBreathing("revive");
  }

  // ── SHOOT combat ──────────────────────────────────────────────────────────────

  private autoFire(time: number): void {
    if (time < this.nextFireAt) return;
    const target = this.nearest(FIRE_RANGE);
    if (!target) return;
    new Shot(this, this.player.x, this.player.y, target, SHOT_SPEED, (t) => this.onShotHit(t));
    AudioFx.shoot();
    this.nextFireAt = time + FIRE_INTERVAL_MS / this.player.fireRateMultiplier(time);
  }

  private onShotHit(target: Pathogen): void {
    if (target.damage(this.player.power)) {
      this.pointsEarned += target.points;
      SaveState.addPoints(target.points);
      this.floatScore(target.x, target.y, target.points, 1);
      target.pop(POP_MS);
    }
  }

  private enemyFire(time: number): void {
    if (time < this.nextVolleyAt) return;
    this.nextVolleyAt = time + ENEMY_VOLLEY_MS;
    const shooters: Pathogen[] = [];
    for (const child of this.pathogens.getChildren()) {
      const p = child as Pathogen;
      if (!p.active || p.removed) continue;
      if (p.distanceTo(this.player.x, this.player.y) <= ENEMY_FIRE_RANGE) shooters.push(p);
    }
    if (shooters.length === 0) return;
    Phaser.Utils.Array.Shuffle(shooters);
    const count = Math.min(ENEMY_SHOTS_PER_VOLLEY_MAX, shooters.length);
    for (let i = 0; i < count; i++) {
      const s = shooters[i];
      new EnemyShot(this, s.x, s.y, this.player, ENEMY_SHOT_SPEED, () => this.onEnemyShotHit());
    }
  }

  private onEnemyShotHit(): void {
    const now = this.time.now;
    if (this.player.takeDamage(ENEMY_SHOT_DAMAGE, now) > 0) {
      AudioFx.hurt();
      if (this.player.health <= 0) this.enterBreathing("revive");
    }
  }

  // ── Shared: enemy removed → sparkle, drop, win check ─────────────────────────

  private onEnemyGone(p: Pathogen): void {
    this.clearedCount++;
    if (this.mode === "engulf") this.player.endDigesting();
    this.feedback.digestBurst(p.x, p.y, Math.min(this.comboCount, 4));
    this.feedback.setInflammation(this.remaining() / Math.max(1, this.totalEnemies));
    if (Math.random() < p.def.dropChance) this.pickups.spawn(p.x, p.y);
    if (this.remaining() === 0) this.winLevel();
  }

  // ── Breathing (revive on fail, or pause-time boost) ──────────────────────────

  private openPauseBreathe(): void {
    if (this.breathing || this.won || this.menuOpen) return;
    this.enterBreathing("boost");
  }

  private enterBreathing(mode: BreathMode): void {
    this.breathing = true;
    this.pending = null;
    this.player.drive(0, 0);
    if (mode === "revive") this.revives++;

    this.events.once(Phaser.Scenes.Events.RESUME, () => {
      this.applyBreatheResult(mode);
      this.breathing = false;
    });
    this.scene.launch("Breathing", { mode, canBoost: !this.breatheUsed });
    this.scene.pause();
  }

  private applyBreatheResult(mode: BreathMode): void {
    if (mode === "revive") {
      if (this.mode === "engulf") {
        this.player.reviveToStart();
        this.comboCount = 0;
        this.separateFromEnemies();
        this.player.startEngulfGrace(ENGULF_GRACE_MS);
      } else {
        this.player.reviveFull(this.time.now);
      }
      this.goldPulse();
    } else if (!this.breatheUsed) {
      this.breatheUsed = true;
      if (this.mode === "engulf") this.player.addStrength(PAUSE_BREATHE_BONUS);
      else this.player.addPower(PAUSE_BREATHE_BONUS);
      this.goldPulse();
    }
  }

  /** Engulf revive: shove the cell clear of the nearest enemy (anti death-loop). */
  private separateFromEnemies(): void {
    const nearest = this.nearest(Infinity);
    if (!nearest) return;
    const safe = (this.player.effectiveRadius + nearest.def.radius + ENGULF_PAD) * 2.2;
    if (nearest.distanceTo(this.player.x, this.player.y) >= safe) return;
    const angle = Phaser.Math.Angle.Between(nearest.x, nearest.y, this.player.x, this.player.y);
    const { width, height } = this.region.map;
    const tx = Phaser.Math.Clamp(nearest.x + Math.cos(angle) * safe, 60, width - 60);
    const ty = Phaser.Math.Clamp(nearest.y + Math.sin(angle) * safe, 60, height - 60);
    this.player.setPosition(tx, ty);
    (this.player.body as Phaser.Physics.Arcade.Body).reset(tx, ty);
  }

  private goldPulse(): void {
    this.player.setTint(COLORS.gold);
    this.time.delayedCall(600, () => this.player.clearTint());
  }

  // ── Quit to body map ──────────────────────────────────────────────────────────

  private openQuitConfirm(): void {
    if (this.breathing || this.won || this.menuOpen) return;
    this.menuOpen = true;
    this.player.drive(0, 0);
    const cx = GAME_WIDTH / 2;
    const cy = GAME_HEIGHT / 2;
    const created: Phaser.GameObjects.GameObject[] = [];
    created.push(
      this.add.rectangle(cx, cy, GAME_WIDTH, GAME_HEIGHT, 0x05080f, 0.8).setScrollFactor(0).setDepth(200).setInteractive(),
      this.add.rectangle(cx, cy, GAME_WIDTH - 90, 360, 0x12233f, 0.98).setStrokeStyle(2, 0x9fd8ff, 0.5).setScrollFactor(0).setDepth(200),
      this.add.text(cx, cy - 110, "Leave this mission?", { fontFamily: "Arial, sans-serif", fontSize: "32px", color: "#eaf6ff", fontStyle: "bold" }).setOrigin(0.5).setScrollFactor(0).setDepth(201),
      this.add.text(cx, cy - 50, "Return to the body map to choose\nanother area. Points earned are kept.", { fontFamily: "Arial, sans-serif", fontSize: "22px", color: "#cfe8ff", align: "center", lineSpacing: 6 }).setOrigin(0.5).setScrollFactor(0).setDepth(201),
    );
    const close = () => {
      created.forEach((o) => o.destroy());
      this.menuOpen = false;
    };
    this.makeOverlayButton(cx, cy + 50, "Return to map", 201, created, () => this.quitToMenu());
    this.makeOverlayButton(cx, cy + 124, "Keep playing", 201, created, close);
  }

  private makeOverlayButton(x: number, y: number, label: string, depth: number, created: Phaser.GameObjects.GameObject[], onClick: () => void): void {
    const bg = this.add
      .rectangle(x, y, 320, 60, 0x1f4f7a, 1)
      .setStrokeStyle(2, 0x9fd8ff, 0.7)
      .setScrollFactor(0)
      .setDepth(depth)
      .setInteractive({ useHandCursor: true });
    const txt = this.add.text(x, y, label, { fontFamily: "Arial, sans-serif", fontSize: "24px", color: "#eaf6ff" }).setOrigin(0.5).setScrollFactor(0).setDepth(depth + 1);
    bg.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, onClick);
    bg.on(Phaser.Input.Events.GAMEOBJECT_POINTER_OVER, () => bg.setFillStyle(0x2a6699));
    bg.on(Phaser.Input.Events.GAMEOBJECT_POINTER_OUT, () => bg.setFillStyle(0x1f4f7a));
    created.push(bg, txt);
  }

  private quitToMenu(): void {
    this.cameras.main.fadeOut(300, 5, 12, 26);
    this.cameras.main.once(Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE, () => this.scene.start("Menu"));
  }

  // ── Win ─────────────────────────────────────────────────────────────────────

  private winLevel(): void {
    if (this.won) return;
    this.won = true;
    this.player.drive(0, 0);
    this.feedback.setInflammation(0);
    this.pointsEarned += MISSION_COMPLETE_BONUS;
    SaveState.addPoints(MISSION_COMPLETE_BONUS);

    this.cameras.main.zoomTo(1.2, 900, "Sine.easeInOut");
    this.time.delayedCall(950, () => this.cameras.main.fadeOut(400, 5, 12, 26));
    this.cameras.main.once(Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE, () => {
      const result: RunResult = {
        region: this.region.name,
        pointsEarned: this.pointsEarned,
        enemiesCleared: this.clearedCount,
        statLabel: this.mode === "engulf" ? "Final strength" : "Final power",
        statValue: this.mode === "engulf" ? this.player.strength : this.player.power,
      };
      this.scene.start("LevelComplete", { result });
    });
  }

  // ── Feedback / HUD ────────────────────────────────────────────────────────────

  private floatScore(x: number, y: number, points: number, mult: number): void {
    const label = mult > 1 ? `+${points}  ×${mult}` : `+${points}`;
    const t = this.add
      .text(x, y, label, { fontFamily: "Arial, sans-serif", fontSize: "26px", color: "#fff4c2", fontStyle: "bold", stroke: "#0a1020", strokeThickness: 4 })
      .setOrigin(0.5)
      .setDepth(95);
    this.tweens.add({ targets: t, y: y - 56, alpha: 0, duration: 850, onComplete: () => t.destroy() });
  }

  private updateMinimap(): void {
    const dots: { x: number; y: number }[] = [];
    for (const child of this.pathogens.getChildren()) {
      const p = child as Pathogen;
      if (p.active && !p.removed) dots.push({ x: p.x, y: p.y });
    }
    this.minimap.update(dots, this.player.x, this.player.y);
  }

  private updateHud(time: number): void {
    const engulf = this.mode === "engulf";
    this.hud.update({
      primaryLabel: engulf ? "STRENGTH" : "POWER",
      primaryValue: engulf ? this.player.strength : this.player.power,
      points: SaveState.points,
      remaining: this.remaining(),
      health: engulf ? 0 : this.player.health,
      maxHealth: engulf ? 0 : this.player.stats.maxHealth,
      shieldSecs: engulf ? this.player.shieldSeconds(time) : 0,
      rapidSecs: engulf ? 0 : this.player.rapidSeconds(time),
      comboMult: engulf && this.comboCount > 1 ? COMBO_MULTS[Math.min(this.comboCount - 1, COMBO_MULTS.length - 1)] : 0,
    });
  }
}
